// Home.js
import { useEffect, useState, useCallback } from "react"
import { useNavigate } from "react-router-dom"
import { motion, useScroll, useSpring } from "framer-motion"
import { collection, getDocs, query, orderBy, limit, startAt, endAt } from "firebase/firestore"
import { db } from "../firebase"
import Facilities from "./Facilities"
import PricingAndTiming from "./PricingAndTiming"
import Testimonials from "./Testimonials"
import LibraryCarousel from "./LibraryCarousel"
import LibraryCard from "./LibraryCard"
import LibraryDetails from "./LibraryDetails"
import SignupModal from "./SignupModal"
import Footer from "./Footer"
import { Helmet } from "react-helmet-async"
import HeroSection from "./home/HeroSection"
import FeaturesSection from "./home/FeaturesSection"

export default function Home() {
  const navigate = useNavigate()
  const [libraries, setLibraries] = useState([])
  const [currentLibraryIndex, setCurrentLibraryIndex] = useState(0)
  const [selectedLibrary, setSelectedLibrary] = useState(null)
  const [searchTerm, setSearchTerm] = useState("")
  const [suggestions, setSuggestions] = useState([])
  const [isTyping, setIsTyping] = useState(false)
  const [isLoading, setIsLoading] = useState(false)
  const [error, setError] = useState(null)
  const [selectedSuggestionIndex, setSelectedSuggestionIndex] = useState(-1)
  const [isSignupModalOpen, setIsSignupModalOpen] = useState(false)

  const { scrollYProgress } = useScroll()
  const scaleX = useSpring(scrollYProgress, {
    stiffness: 100,
    damping: 30,
    restDelta: 0.001,
  })

  useEffect(() => {
    const fetchLibraries = async () => {
      try {
        const querySnapshot = await getDocs(query(collection(db, "libraries"), orderBy("name"), limit(10)))
        const librariesData = querySnapshot.docs.map((doc) => ({ id: doc.id, ...doc.data() }))
        setLibraries(librariesData)
      } catch (error) {
        console.error("Error fetching libraries:", error)
        setError("Failed to fetch libraries. Please try again later.")
      }
    }

    fetchLibraries()
  }, [])

  useEffect(() => {
    if (libraries.length > 0) {
      const intervalId = setInterval(() => {
        setCurrentLibraryIndex((prevIndex) => (prevIndex + 1) % libraries.length)
      }, 5000)

      return () => clearInterval(intervalId)
    }
  }, [libraries])

  const fetchSuggestions = useCallback(async (term) => {
    if (term.length < 1) {
      setSuggestions([])
      return
    }

    setIsLoading(true)
    setError(null)

    try {
      // String.fromCharCode avoids encoding issues with the Firestore sentinel character
      const sentinel = String.fromCharCode(0xf8ff)

      // Two prefix queries: as-typed and title-case so 'city' matches 'City Library'
      const termAsTyped   = term
      const termTitleCase = term.charAt(0).toUpperCase() + term.slice(1).toLowerCase()

      const makeQuery = (prefix) => query(
        collection(db, 'libraries'),
        orderBy('name'),
        startAt(prefix),
        endAt(prefix + sentinel),
        limit(5)
      )

      const [snap1, snap2] = await Promise.all([
        getDocs(makeQuery(termAsTyped)),
        getDocs(makeQuery(termTitleCase)),
      ])

      // Merge both result sets, de-duplicate by document ID
      const seen = new Set()
      const results = [...snap1.docs, ...snap2.docs]
        .map(d => ({ id: d.id, ...d.data() }))
        .filter(lib => {
          if (seen.has(lib.id)) return false
          seen.add(lib.id)
          return true
        })

      setSuggestions(results.slice(0, 2))
      if (results.length === 0) {
        setError('No libraries found. Please try a different search term.')
      }
    } catch (error) {
      console.error('Error fetching suggestions:', error)
      setError('Failed to fetch suggestions. Please try again.')
    } finally {
      setIsLoading(false)
    }
  }, [])

  useEffect(() => {
    const debounceTimer = setTimeout(() => {
      if (searchTerm) {
        fetchSuggestions(searchTerm)
      } else {
        setSuggestions([])
        setError(null)
      }
    }, 300)

    return () => clearTimeout(debounceTimer)
  }, [searchTerm, fetchSuggestions])

  const handleSuggestionClick = (library) => {
    setSelectedLibrary(library)
    setSearchTerm("")
    setSuggestions([])
    setError(null)
    setIsTyping(false)
    window.location.href = `/library/${library.slug || library.id}`
  }

  const handleKeyDown = (e) => {
    if (e.key === "ArrowDown") {
      e.preventDefault()
      setSelectedSuggestionIndex((prev) => (prev < suggestions.length - 1 ? prev + 1 : prev))
    } else if (e.key === "ArrowUp") {
      e.preventDefault()
      setSelectedSuggestionIndex((prev) => (prev > 0 ? prev - 1 : prev))
    } else if (e.key === "Enter" && selectedSuggestionIndex !== -1) {
      handleSuggestionClick(suggestions[selectedSuggestionIndex])
    } else if (e.key === "Escape") {
      setIsTyping(false)
    }
  }

  const handleViewMore = () => {
    navigate("/libraries")
  }

  const handleViewAllLibraries = () => {
    window.open("/libraries", "_blank")
  }

  const handleSignupClick = () => {
    setIsSignupModalOpen(true)
  }

  const handleLearnMoreClick = () => {
    navigate("/why-join")
  }

  return (
    <div className="min-h-screen bg-gradient-to-b from-blue-900 via-indigo-900 to-purple-900 text-white overflow-hidden">
      <Helmet>
        <title>The Library Hub – Find & Book Libraries Near You</title>
        <meta name="description" content="Discover and book libraries near you across India. Find study spaces, check real-time seat availability, and manage subscriptions seamlessly with The Library Hub." />
        <link rel="canonical" href="https://thelibraryhub.in/" />
        <meta property="og:title" content="The Library Hub – Find & Book Libraries Near You" />
        <meta property="og:description" content="Discover libraries across India, book seats in real time, and manage your study subscriptions — all in one place." />
        <meta property="og:url" content="https://thelibraryhub.in/" />
      </Helmet>
      <motion.div className="fixed top-0 left-0 right-0 h-1 bg-blue-500 origin-left z-50" style={{ scaleX }} />
      <div className="container relative mx-auto px-4 py-12">
        <HeroSection
          searchTerm={searchTerm}
          setSearchTerm={setSearchTerm}
          suggestions={suggestions}
          isTyping={isTyping}
          setIsTyping={setIsTyping}
          isLoading={isLoading}
          error={error}
          selectedSuggestionIndex={selectedSuggestionIndex}
          setSelectedSuggestionIndex={setSelectedSuggestionIndex}
          handleSuggestionClick={handleSuggestionClick}
          handleKeyDown={handleKeyDown}
          handleViewMore={handleViewMore}
        />

        {selectedLibrary && (
          <motion.section
            className="mb-16 md:mb-32"
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
          >
            <h2 className="text-2xl md:text-3xl lg:text-4xl font-bold text-center mb-8 md:mb-12">Selected Library</h2>
            <LibraryDetails library={selectedLibrary} />
          </motion.section>
        )}

        <FeaturesSection
          handleViewAllLibraries={handleViewAllLibraries}
          handleSignupClick={handleSignupClick}
          handleLearnMoreClick={handleLearnMoreClick}
          libraries={libraries}
          currentLibraryIndex={currentLibraryIndex}
          LibraryCard={LibraryCard}
        />

        <motion.section
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8 }}
          viewport={{ once: true, amount: 0.2 }}
          className="mb-16 md:mb-32"
        >
          <Facilities />
        </motion.section>

        <motion.section
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8 }}
          viewport={{ once: true, amount: 0.2 }}
          className="mb-16 md:mb-32 mt-16 md:mt-32"
        >
          <PricingAndTiming />
        </motion.section>

        <motion.section
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8 }}
          viewport={{ once: true, amount: 0.2 }}
          className="mb-16 md:mb-32"
        >
          <Testimonials />
        </motion.section>

        <motion.section
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8 }}
          viewport={{ once: true, amount: 0.2 }}
          className="mb-16 md:mb-32"
        >
          <LibraryCarousel />
        </motion.section>
      </div>

      <Footer />
      <SignupModal isOpen={isSignupModalOpen} onClose={() => setIsSignupModalOpen(false)} />
    </div>
  )
}

